import { useLocation, useNavigate } from 'react-router-dom';
import { usePMAuth } from '@/contexts/PMAuthContext';
import {
  LayoutDashboard, Users, CreditCard, Package, ArrowLeftRight,
  AlertTriangle, Sliders, BarChart3, UserPlus, Settings,
  ChevronRight, Key, FileWarning, Scale, ClipboardCheck,
  Receipt, ClipboardList, Palette, Webhook, Landmark, FileArchive, Activity,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';

const pmNav = [
  { label: 'Command Center', icon: LayoutDashboard, path: '/pm/command-center', permission: 'pm.dashboard.view' },
  { label: 'Dashboard', icon: Activity, path: '/pm', permission: 'pm.dashboard.view' },
  { label: 'End Users', icon: Users, path: '/pm/end-users', permission: 'pm.end_users.view' },
  { label: 'Cards', icon: CreditCard, path: '/pm/cards', permission: 'pm.cards.view' },
  { label: 'Orders', icon: Package, path: '/pm/orders', permission: 'pm.orders.view' },
  { label: 'Transactions', icon: ArrowLeftRight, path: '/pm/transactions', permission: 'pm.transactions.view' },
  { label: 'Disputes', icon: AlertTriangle, path: '/pm/disputes', permission: 'pm.disputes.view' },
  { label: 'Fraud & Monitoring', icon: FileWarning, path: '/pm/fraud', permission: 'pm.fraud.view' },
  { label: 'Controls', icon: Sliders, path: '/pm/controls', permission: 'pm.cards.controls.manage' },
  { label: 'Change Requests', icon: ClipboardList, path: '/pm/requests', permission: 'pm.controls.request' },
  { label: 'Reports', icon: BarChart3, path: '/pm/reports', permission: 'pm.reports.view' },
  { label: 'Analytics', icon: BarChart3, path: '/pm/analytics', permission: 'pm.reports.view' },
  { label: 'Scheduled Reports', icon: ClipboardList, path: '/pm/scheduled-reports', permission: 'pm.reports.view' },
  { label: 'Compliance', icon: Scale, path: '/pm/compliance', permission: 'pm.compliance.view' },
  { label: 'Team', icon: UserPlus, path: '/pm/team', permission: 'pm.team.view' },
  { label: 'API', icon: Key, path: '/pm/api', permission: 'pm.api.view' },
  { label: 'Settlement', icon: Landmark, path: '/pm/settlement', permission: 'pm.billing.view' },
  { label: 'Billing', icon: Receipt, path: '/pm/billing', permission: 'pm.billing.view' },
  { label: 'Statements', icon: FileArchive, path: '/pm/statements', permission: 'pm.billing.view' },
  { label: 'Audit Log', icon: ClipboardCheck, path: '/pm/audit', permission: 'pm.audit.view' },
  { label: 'Approvals', icon: Scale, path: '/pm/approvals', permission: 'pm.approvals.view' },
  { label: 'Settings', icon: Settings, path: '/pm/settings', permission: 'pm.settings.view' },
  { label: 'Branding', icon: Palette, path: '/pm/settings/branding', permission: 'pm.settings.view' },
  { label: 'Webhooks', icon: Webhook, path: '/pm/settings/webhooks', permission: 'pm.api.view' },
];



export function PMSidebar() {
  const location = useLocation();
  const navigate = useNavigate();
  const { hasPermission, programs } = usePMAuth();


  // Use exact match for index + parent-only paths; use exact startsWith for leaf paths
  const isActive = (path: string) => {
    if (path === '/pm') return location.pathname === '/pm';
    if (path === '/pm/settings') return location.pathname === '/pm/settings';
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };


  return (
    <nav className="w-[240px] border-r border-border/50 bg-card/30 p-3 space-y-0.5 shrink-0 overflow-y-auto">
      <div className="px-2 pb-3 mb-2 border-b border-border/50">
        <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-widest">
          Program Manager
        </p>
        <Badge variant="outline" className="text-[9px] mt-1">
          {programs.length} program{programs.length !== 1 ? 's' : ''}
        </Badge>
      </div>
      {pmNav.map(item => {
        if (!hasPermission(item.permission)) return null;
        const active = isActive(item.path);
        return (
          <button
            key={item.path}
            onClick={() => navigate(item.path)}
            className={`w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-sm font-medium transition-all ${
              active
                ? 'bg-accent/15 text-accent'
                : 'text-muted-foreground hover:text-foreground hover:bg-secondary/60'
            }`}
          >
            <item.icon className="w-4 h-4" />
            <span className="flex-1 text-left">{item.label}</span>
            {active && <ChevronRight className="w-3 h-3 opacity-50" />}
          </button>
        );
      })}
    </nav>
  );
}
